import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";

const FIELD_ICON_CLASS = "sign-in-input-icon absolute left-3 h-4 w-4";

function Input({ className, type, ...props }) {
    return (
        <input
            type={type}
            data-slot="input"
            className={cn(
                "sign-in-input-field flex h-10 w-full min-w-0 rounded-lg border border-white/10 bg-white/5 py-1 text-base text-white shadow-sm outline-none transition-[color,box-shadow] placeholder:text-white/30 md:text-sm",
                "focus-visible:border-white/20 focus-visible:ring-[3px] focus-visible:ring-white/20",
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            {...props}
        />
    );
}

/**
 * Animated sign-in card (glass + 3D tilt). Keeps YesMLS login options only.
 */
export function SignInCard({
    email,
    onEmailChange,
    password,
    onPasswordChange,
    showPassword,
    onTogglePassword,
    onSubmit,
    isLoading = false,
    logoSrc,
    title = "Welcome Back!",
    description = "Please log in to your account to continue.",
    forgotPasswordTo = "/forgot-password",
    signupTo = "/signup",
    embedded = false,
}) {
    const [focusedInput, setFocusedInput] = useState(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
    const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left - rect.width / 2);
        mouseY.set(e.clientY - rect.top - rect.height / 2);
    };

    const handleMouseLeave = () => {
        mouseX.set(0);
        mouseY.set(0);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        onSubmit?.(e);
    };

    const outerClass = embedded
        ? "relative z-10 flex w-full items-center justify-center px-4 py-8"
        : "relative flex min-h-screen w-screen items-center justify-center overflow-hidden bg-black";

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={outerClass}
            style={{ perspective: 1500 }}
        >
            {!embedded && (
                <>
                    <motion.div className="absolute inset-0 bg-gradient-to-b from-purple-500/40 via-purple-700/50 to-black" />
                    <motion.div
                        className="absolute top-0 left-1/2 h-[60vh] w-[120vh] -translate-x-1/2 rounded-b-[50%] bg-purple-400/20 blur-[80px]"
                        animate={{ opacity: [0.15, 0.3, 0.15] }}
                        transition={{ duration: 8, repeat: Infinity, repeatType: "mirror" }}
                    />
                </>
            )}

            <motion.div
                className="relative w-full max-w-sm"
                style={{ rotateX, rotateY }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                whileHover={{ z: 10 }}
            >
                <div className="group relative">
                    <motion.div
                        className="absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-700 group-hover:opacity-70"
                        animate={{
                            boxShadow: [
                                "0 0 10px 2px rgba(255,255,255,0.03)",
                                "0 0 15px 5px rgba(255,255,255,0.05)",
                                "0 0 10px 2px rgba(255,255,255,0.03)",
                            ],
                        }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatType: "mirror" }}
                    />

                    <motion.div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/50 p-6 shadow-2xl backdrop-blur-xl">
                        <div className="mb-5 space-y-1 text-center">
                            <motion.div
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", duration: 0.8 }}
                                className="relative mx-auto mb-3 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5"
                            >
                                {logoSrc ? (
                                    <img src={logoSrc} alt="YesMLS Pro" className="h-10 w-auto object-contain" />
                                ) : (
                                    <span className="bg-gradient-to-b from-white to-white/70 bg-clip-text text-lg font-bold text-transparent">
                                        Y
                                    </span>
                                )}
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="bg-gradient-to-b from-white to-white/80 bg-clip-text text-xl font-bold text-transparent"
                            >
                                {title}
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="text-xs text-white/60"
                            >
                                {description}
                            </motion.p>
                        </div>

                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div className="space-y-3">
                                <motion.div
                                    className={cn("relative", focusedInput === "email" && "z-10")}
                                    whileHover={{ scale: 1.01 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                >
                                    <div className="sign-in-input-wrap relative flex items-center overflow-hidden rounded-lg">
                                        <Mail className={FIELD_ICON_CLASS} />
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="Enter your email"
                                            value={email}
                                            onChange={(e) => onEmailChange?.(e.target.value)}
                                            onFocus={() => setFocusedInput("email")}
                                            onBlur={() => setFocusedInput(null)}
                                            className="border-transparent pl-10 pr-3 focus:border-white/20 focus:bg-white/10"
                                            autoComplete="email"
                                            required
                                        />
                                    </div>
                                </motion.div>

                                <motion.div
                                    className={cn("relative", focusedInput === "password" && "z-10")}
                                    whileHover={{ scale: 1.01 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                >
                                    <div className="sign-in-input-wrap relative flex items-center overflow-hidden rounded-lg">
                                        <Lock className={FIELD_ICON_CLASS} />
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            name="password"
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => onPasswordChange?.(e.target.value)}
                                            onFocus={() => setFocusedInput("password")}
                                            onBlur={() => setFocusedInput(null)}
                                            className="border-transparent pl-10 pr-10 focus:border-white/20 focus:bg-white/10"
                                            autoComplete="current-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 text-white/40 transition-colors hover:text-white"
                                            onClick={onTogglePassword}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                <Eye className="h-4 w-4" />
                                            ) : (
                                                <EyeOff className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </motion.div>
                            </div>

                            <div className="text-end">
                                <Link
                                    to={forgotPasswordTo}
                                    className="text-xs text-white/60 transition-colors hover:text-white"
                                >
                                    Forgot Password?
                                </Link>
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                disabled={isLoading}
                                className="group/button login-btn relative mt-2 w-full"
                            >
                                <div className="relative flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white font-medium text-black transition-all">
                                    <AnimatePresence mode="wait">
                                        {isLoading ? (
                                            <motion.div
                                                key="loading"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="h-4 w-4 animate-spin rounded-full border-2 border-black/70 border-t-transparent"
                                            />
                                        ) : (
                                            <motion.span
                                                key="button-text"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="flex items-center gap-1 text-sm font-medium"
                                            >
                                                Login
                                                <ArrowRight className="h-3 w-3 transition-transform group-hover/button:translate-x-1" />
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </motion.button>

                            <motion.p
                                className="mt-4 text-center text-xs text-white/60"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                            >
                                Don&apos;t have an account?{" "}
                                <Link
                                    to={signupTo}
                                    className="group/signup relative inline-block font-medium text-white"
                                >
                                    <span className="transition-colors group-hover/signup:text-white/70">
                                        Sign up
                                    </span>
                                    <span className="absolute bottom-0 left-0 h-px w-0 bg-white transition-all group-hover/signup:w-full" />
                                </Link>
                            </motion.p>
                        </form>
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
}
